import {Form} from './form';
import {Config} from '../config';
import {AddonView} from '../view/addon-view';
import {AddonValidator} from '../validators/addon-validator';
import {Order} from '../config/order';
import { Product } from '../config/product';
import { FormTypes } from './form-types';
import { ProductType } from '../config/product-type';
import { QuantityForm } from './quantity-form';
import { QuantityAddonValidator } from '../validators/quantity-addon-validator';

export class AddonForm extends Form {

    type = FormTypes.Addon;
    view: AddonView = new AddonView();
    addonProducts: any[] = [];
    quantityFormsWrapper: any[] = [];
    validator: any = new AddonValidator();

    constructor(step: number | null = null) {
        super();
        this.validators.push(this.validator);
        this.step = step;
    }

    init(config: Config, callbacks: any) {
        super.init(config, callbacks);
        this.addonProducts = this.config.products.filter((product: Product) => product.addon && !product.prices);
        if (this.config.products.length !== this.addonProducts.length) {
            this.validationContext = [];
            this.validationContext.push('valid');
        }
        return Promise.resolve();
    }

    async render() {
        this.view.renderProductsListForm(this.addonProducts, this.config.settings.currency, this.calculateProducts.bind(this));
        this.validationContext = [];
        this.view.hideErrorAddon();
        this.addonProducts.forEach(async (product: Product) => {
            if (product.showQuantity) {
                const addonId = this.generateAddonId(product);
                const form = new QuantityForm();
                this.quantityFormsWrapper.push({
                    id: addonId,
                    form: form,
                    productName: product.name
                });
                const callback: any = {};
                callback[`setQuantity_${addonId}`] = this.setQuantity.bind(this, product, addonId);
                await form.init({}, callback);
            }
        });
        this.quantityFormsWrapper.forEach(async (quantityFormWrapper: any) => {
            const quantityElementId = quantityFormWrapper.id;
            this.validators.push(new QuantityAddonValidator(quantityElementId));
            await quantityFormWrapper.form.render(quantityElementId, quantityFormWrapper.productName, true);
            this.hideQuantity(quantityElementId);
        });

        if (this.step){
            this.hideForm();
        }
        
        this.view.preselectDfProductId();

        return Promise.resolve();
    }
    
    async calculateProducts(event: any) {
        const addonId = event.target.getAttribute('addon-id');
        const quantity = this.getQuantity(addonId);
        if (event.target.checked) {
            this.paymentData.amount += +event.target.value;
            if (this.isSinglePaymentType(event)) {
                this.paymentData.singlePaymentAmount += +event.target.value * quantity;
            } else {
                this.paymentData.subscriptionAmount += +event.target.value * quantity;
            }
            this.view.hideErrorAddon();
            this.showQuantity(addonId);
        }
        if (!event.target.checked) {
            this.paymentData.amount -= +event.target.value;
            if (this.isSinglePaymentType(event)) {
                this.paymentData.singlePaymentAmount -= +event.target.value * quantity;
            } else {
                this.paymentData.subscriptionAmount -= +event.target.value * quantity;
            }
            this.hideQuantity(addonId);
            this.resetQuantity(addonId);
        }
        if (this.config.products.length === this.addonProducts.length) {
            const checkboxCollection = this.view.getCheckboxesForm();
            this.validationContext = Array.prototype.filter.call(checkboxCollection, (checkbox: any) => {
                return checkbox.checked;
            });
            if (!this.validationContext.length) {
                this.view.showErrorAddon();
            } else {
                this.view.hideErrorAddon();
            }
        }
        this.triggerEvent('resetDiscount');
        this.triggerEvent('amountChange');
    }

    isSinglePaymentType(event: any) {
        const paymentType = event.target.getAttribute('payment-type');
        return paymentType && paymentType === ProductType.SINGLE_PAYMENT;
    }

    async validate(): Promise<boolean> {
        if (this.config.products.length === this.addonProducts.length) {
            this.view.hideErrorAddon();
            let valid = await this.validator.validate(this.validationContext);
            if (!valid) {
                this.view.showErrorAddon();
                return Promise.resolve(valid);
            }
            if (this.validators.length > 1) {
                valid = this.validateAddons(valid);
            }
            return Promise.resolve(valid);
        } else {
            return Promise.resolve(true);
        }
    }

    validateAddons = async (valid: boolean) => {
        this.validators.shift();
        valid = await super.validate();
        this.validators.unshift(this.validator);
        return valid;
    }

    submit(order: Order) {
        let selectedProducts = this.getSelectedProducts();
        selectedProducts = this.setDiscount(selectedProducts);

        selectedProducts.forEach((product:any) => {
            order.selectedProducts.push(product);
        });

        return Promise.resolve(order);
    }

    getSelectedProducts(): any[] {
        let resp: any = [];
        const dfProducts = this.getSelectedBoxes();
        if (dfProducts) {
            resp = dfProducts.map( (item: any) => {
                return {
                    dfProductId: item.getAttribute('df_product_id'),
                    type: item.getAttribute('payment-type'),
                    name : item.getAttribute('name'),
                    amount: item.getAttribute('value'),
                    subscriptionPlanId: item.getAttribute('id') && item.getAttribute('id') !== 'undefined' ? item.getAttribute('id') : null,
                    quantity: this.getQuantity(item.getAttribute('addon-id'))
                };
            });
        }
        return resp;
    }
    getSelectedBoxes() {
        const checkboxes = this.view.getCheckboxesForm();
        return this.getCheckedBoxes(checkboxes);
    }

    getCheckedBoxes(chBoxes: any) {
        const checkboxes: any = Array.prototype.slice.call( chBoxes );
        const checkboxesChecked = checkboxes.filter( (item: any) => item.checked);
        return checkboxesChecked.length > 0 ? checkboxesChecked : null;
    }
    
    private setDiscount(selectedProducts: Array<any>) {
        if (this.config.settings && this.config.settings.discountId) {
            const coupon = this.config.settings.discountId;
            return selectedProducts.map((item: any) => {
                item.discountId = coupon.id;
                return item;
            });
        }
        return selectedProducts;
    }

    setQuantity(product: Product, id?: string) {
        const selectedBoxes = this.getSelectedBoxes();
        if (!selectedBoxes || selectedBoxes.length === 0) {
            return;
        }
        if (!selectedBoxes.find((selectedBox: any) => selectedBox.getAttribute('addon-id') === id)) {
            return;
        }
        const quantityForm = this.findQuantityForm(id);
        if (!quantityForm) {
            return;
        }
        const quantity = quantityForm.quantity;
        const previousQuantity = quantityForm.previousQuantity;

        this.paymentData.quantity = quantity;

        if (product.type === ProductType.SINGLE_PAYMENT) {
            this.paymentData.singlePaymentAmount += product.amount * (quantity - previousQuantity);
        } else {
            this.paymentData.subscriptionAmount += product.amount * (quantity - previousQuantity);
        }
        this.triggerEvent('amountChange');
    }

    generateAddonId(product: Product) {
        return this.view.generateAddonId(product);
    }

    findQuantityForm(id?: string) {
        const quantityFormWrapper = this.quantityFormsWrapper.find((formWrapper: any) => formWrapper.id === id);
        return quantityFormWrapper ? quantityFormWrapper.form : null;
    }

    getQuantity(id: string) {
        const quantityForm = this.findQuantityForm(id);
        return quantityForm ? quantityForm.quantity : 1;
    }

    showQuantity(id: string) {
        const element: any = document.querySelector(`#df_${id}_quantity_container`);
        if (element) {
            element.style.display = 'block';
        }
    }

    hideQuantity(id: string) {
        const element: any = document.querySelector(`#df_${id}_quantity_container`);
        if (element) {
            element.style.display = 'none';
        }
    }

    resetQuantity(id: string) {
        const element = document.querySelector(`#df_${id}_quantity`) as HTMLSelectElement;
        if (element) {
            const initalQuantity = 1;
            element.value = `${initalQuantity}`;
            const quantityForm = this.findQuantityForm(id) as QuantityForm;
            quantityForm.quantity = initalQuantity;
            quantityForm.validate();
        }
    }

    showForm() {
        this.view.showForm();
        if (this.quantityFormsWrapper.length) {
            this.quantityFormsWrapper.forEach(x => x.form.showForm());
        }
    }

    hideForm() {
        this.view.hideForm();
        if (this.quantityFormsWrapper.length) {
            this.quantityFormsWrapper.forEach(x => x.form.hideForm());
        }
    }

}
